import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import './Projects.css';
import SearchBar from 'components/shared/navigation/SearchBar';
import SimpleTable from 'components/shared/table/SimpleTable';
import NavigationBar from 'components/shared/navigation/NavigationBar';
import { getCurrentUser } from 'selectors/authentication';
import { usersUtil, TYPES } from 'services/utils/users-util';
import { Enterprise } from 'domain/enterprise';
import { getNotifications } from 'selectors/notifications';
import { unreadNotifications } from 'domain/notification';
import {
  getCurrentPage,
  getProjects,
  getCurrentNav,
} from 'components/admin/projects/store/selectors';
import {
  loadProjectsRequest,
  addProject,
  goToProjectDetails,
  changeNav,
} from 'components/admin/projects/store/actions';
import SimpleText from 'components/shared/text/SimpleText';
import SimplePagination from 'components/shared/pagination/SimplePagination';
import { simpleScrollToTop } from 'services/utils/scroller-util';
import BaseStatus from 'components/shared/text/BaseStatus';
import ProjectStatus from 'components/shared/project-status/ProjectStatus';
import { ProjectStatusEnum } from 'domain/project-status-enum';
import logoPng from 'components/header/logo.png';

class Projects extends Component {
  static propTypes = {
    perPage: PropTypes.number,
    showTitle: PropTypes.bool,
    showControls: PropTypes.bool,
    showNav: PropTypes.bool,
  };

  static defaultProps = {
    perPage: 25,
    showTitle: true,
    showControls: true,
    showNav: true,
  };

  constructor(props) {
    super(props);

    this.userIsGC = usersUtil.hasType(
      props.currentUser,
      TYPES.GENERAL_CONTRACTOR
    );
    this.userIsClient = usersUtil.hasType(props.currentUser, TYPES.CLIENT);
    this.showActions = Enterprise.showActions(props.currentUser);
    this.showMyProjects = Enterprise.showMyProjects(props.currentUser);
    this.initialTabSelection = this.showMyProjects ? 8 : 4;

    this.state = { nameToSearch: '' };
  }

  componentDidMount() {
    this.handleNavSelect(this.initialTabSelection);
  }

  componentDidUpdate() {
    this.props.showControls && simpleScrollToTop();
  }

  handleGenerateLink = (index) => {
    const {
      projects: { content },
    } = this.props;
    return `projects/${content[index].id}`;
  };

  handleNavSelect = (id) => {
    this.loadProjects(id, 1);
    this.props.dispatch(changeNav(id));
  };

  handlePageChange = (newPage) => {
    this.loadProjects(this.props.currentNav, newPage);
  };

  loadProjects(tabSelection, newPage) {
    const { perPage, dispatch } = this.props;
    const { nameToSearch } = this.state;
    const filters = {
      page: newPage,
      per_page: perPage,
      by_name: nameToSearch,
    };
    switch (tabSelection) {
      case 1:
        dispatch(loadProjectsRequest(filters));
        break;

      case 2:
        dispatch(loadProjectsRequest({ ...filters, pending: true }));
        break;

      case 3:
        dispatch(loadProjectsRequest({ ...filters, precon: true }));
        break;

      case 4:
        dispatch(loadProjectsRequest({ ...filters, in_progress: true }));
        break;

      case 5:
        dispatch(loadProjectsRequest({ ...filters, cancelled: true }));
        break;

      case 6:
        dispatch(loadProjectsRequest({ ...filters, lost: true }));
        break;

      case 7:
        dispatch(loadProjectsRequest({ ...filters, complete: true }));
        break;

      case 8:
        dispatch(loadProjectsRequest({ ...filters, my_projects: true }));
        break;

      default:
    }
  }

  handleAddProject = () => {
    this.props.dispatch(addProject());
  };

  handleRowSelection = (index) => {
    this.props.dispatch(
      goToProjectDetails(this.props.projects.content[index].id)
    );
  };

  handleSearchTextChange = (value) => {
    const { dispatch } = this.props;
    this.setState({ nameToSearch: value }, () => this.loadProjects(1, 1));
    dispatch(changeNav(1));
  };

  handleSearchTextClear = () => {
    this.handleSearchTextChange('');
  };

  render() {
    const { nameToSearch } = this.state;
    const { showTitle, showControls, showNav } = this.props;
    return (
      <div className="projects">
        {showTitle && <div className="full-title">Projects</div>}

        {this.props.projects !== null && (
          <div className="content-container">
            {showNav && (
              <SearchBar
                addButton={
                  this.showActions &&
                  showControls && (
                    <input
                      className={'btn button button-orange'}
                      type="button"
                      value="Add Project"
                      onClick={this.handleAddProject}
                    />
                  )
                }
                searchPlaceHolder="Search by Name"
                searchText={nameToSearch}
                onSearchTextChange={this.handleSearchTextChange}
                onClearText={this.handleSearchTextClear}
                onSearch={showControls ? this.handleSearchTextChange : null}
              >
                <NavigationBar
                  items={[
                    {
                      id: 1,
                      title: 'All',
                    },
                    this.showMyProjects && {
                      id: 8,
                      title: 'My Projects',
                      total: this.props.projects.totalMyProjects,
                    },
                    {
                      id: 2,
                      show: this.props.projects.totalPending > 0,
                      title: 'Bid/Budget',
                      total: this.props.projects.totalPending,
                    },
                    {
                      id: 3,
                      show: this.props.projects.totalPrecon > 0,
                      title: 'Precon',
                      total: this.props.projects.totalPrecon,
                    },
                    {
                      id: 4,
                      title: 'In Progress',
                      total: this.props.projects.totalInProgress,
                    },
                    {
                      id: 7,
                      show: this.props.projects.totalComplete > 0,
                      title: 'Complete',
                      total: this.props.projects.totalComplete,
                    },
                    {
                      id: 5,
                      show: this.props.projects.totalCancelled > 0,
                      title: 'Cancelled',
                      total: this.props.projects.totalCancelled,
                    },
                    {
                      id: 6,
                      show: this.props.projects.totalLost > 0,
                      title: 'Lost',
                      total: this.props.projects.totalLost,
                    },
                  ].filter((item) => item)}
                  selectedValue={this.props.currentNav}
                  onSelected={this.handleNavSelect}
                />
              </SearchBar>
            )}

            {this.props.projects.total > 0 && (
              <React.Fragment>
                <SimpleTable
                  className="projects-table"
                  headers={[
                    'PROJECT #',
                    'NAME',
                    !this.userIsGC && 'GC NAME',
                    !this.userIsClient && 'CLIENT NAME',
                    'LOCATION',
                    'STATUS',
                  ].filter((header) => !!header)}
                  rows={this.mapProjects(this.props.projects.content)}
                  emptyMessage="No Projects"
                  onRowSelected={this.handleRowSelection}
                  onGenerateLink={this.handleGenerateLink}
                  newView
                />
                <div className="pagination-container">
                  <SimplePagination
                    currentPage={this.props.currentPage}
                    pageSize={this.props.projects.pageSize}
                    total={this.props.projects.total}
                    onPageChange={this.handlePageChange}
                  />
                </div>
              </React.Fragment>
            )}
          </div>
        )}

        {this.props.projects !== null && this.props.projects.total === 0 && (
          <div className="zero-state">
            <div className="zero-state-content">
              <img src={logoPng} className="logo" alt="Spackle Logo" />
              <div>
                We’re excited to have you join the Spackle Enterprise Platform.
              </div>
              {this.showActions && (
                <div>
                  <div>Click button below to create your first project!</div>
                  <input
                    className={'btn button button-orange'}
                    type="button"
                    value="Create Project"
                    onClick={this.handleAddProject}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  mapProjects = (projects) => {
    const unreadById = unreadNotifications(
      this.props.notifications,
      'Project',
      'project_id'
    );
    return projects.map((project) => {
      const unread = unreadById && !!unreadById[project.id];
      return [
        <div>
          {unread && (
            <BaseStatus
              labels={[
                {
                  id: 'unread',
                  label: '',
                },
              ]}
              status="unread"
            />
          )}
          {project.number}
        </div>,
        project.name,
        !this.userIsGC &&
          project.general_contractor &&
          project.general_contractor.name,
        !this.userIsClient && project.client.company_name,
        <SimpleText
          lines={[
            project.building.location.street_address_1,
            project.building.location.city,
          ]}
        />,
        <ProjectStatus status={ProjectStatusEnum.create(project.status)} />,
      ].filter((attr) => attr);
    });
  };
}

export default connect((state) => {
  return {
    currentUser: getCurrentUser(state),
    currentPage: getCurrentPage(state),
    projects: getProjects(state),
    notifications: getNotifications(state),
    currentNav: getCurrentNav(state),
  };
})(Projects);



// WEBPACK FOOTER //
// ./src/components/admin/projects/Projects.js